import React, { type ReactNode, useState } from 'react';
import { Pressable } from 'react-native';
import {
    BodyMediumRegular,
    Icon,
    Info,
    LabelRegular,
    LoadingSkeleton,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { centsToFormattedDollars } from '../../../common/utils/bankingUtils';
import type {
    FreeToSpendDataPoint,
    FreeToSpendPeriod,
    FreeToSpendStatus,
} from '../../types/FreeToSpendTypes';
import { BudgetGraph } from './BudgetGraph';

const GRAPH_HEIGHT = 108;

const STATUS_LABEL: Record<FreeToSpendStatus, string> = {
    onTrack: 'On track',
    slowDown: 'Slow down',
    over: 'Over',
};

/** The colored status pill (On track / Slow down / Over). */
export const SpendingStatusPill = ({
    status,
}: {
    status: FreeToSpendStatus;
}) => {
    const { getColor } = useThemeColors();
    const backgroundColor =
        status === 'onTrack'
            ? '$green900'
            : status === 'over'
              ? '$orange400'
              : '$lemon';

    return (
        <View
            backgroundColor={backgroundColor}
            borderRadius={9999}
            paddingHorizontal={12}
            paddingVertical={4}
        >
            <XStack gap={8} alignItems="center" justifyContent="center">
                <BodyMediumRegular
                    color={
                        status === 'slowDown'
                            ? undefined
                            : status === 'onTrack'
                              ? '$white'
                              : '$orange1100'
                    }
                    style={
                        status === 'slowDown'
                            ? { color: getColor('$lemonDarkest') }
                            : undefined
                    }
                    fontSize={16}
                    fontWeight="600"
                    lineHeight={24}
                >
                    {STATUS_LABEL[status]}
                </BodyMediumRegular>
                {status !== 'onTrack' && (
                    <Icon
                        icon={Info}
                        size={16}
                        color={status === 'over' ? '$orange1100' : undefined}
                        stringColor={
                            status === 'slowDown'
                                ? getColor('$lemonDarkest')
                                : undefined
                        }
                    />
                )}
            </XStack>
        </View>
    );
};

export interface SpendingSummaryViewProps {
    title: string;
    headerSlot?: ReactNode;
    status: FreeToSpendStatus | null;
    onStatusPress?: () => void;
    currentSpendLabel: string;
    currentSpendCents: number | null;
    budgetCents: number | null;
    spendData: FreeToSpendDataPoint[];
    period: FreeToSpendPeriod;
    isOverBudget: boolean;
    leftInBudgetCents: number | null;
    timeLeft: string | null;
    remainingLabel: string;
    remainingValue: string | null;
    onGraphPress?: () => void;
    anchorDate?: Date;
    isLoading?: boolean;
}

/**
 * Presentational free-to-spend summary card. Owns layout only — all data and
 * interactivity are supplied via props so it can be driven by the home screen's
 * hooks/atoms or by agent-supplied props in the Sprout chat widget.
 */
export const SpendingSummaryView = ({
    title,
    headerSlot,
    status,
    onStatusPress,
    currentSpendLabel,
    currentSpendCents,
    budgetCents,
    spendData,
    period,
    isOverBudget,
    leftInBudgetCents,
    timeLeft,
    remainingLabel,
    remainingValue,
    onGraphPress,
    anchorDate,
    isLoading = false,
}: SpendingSummaryViewProps) => {
    const [chartWidth, setChartWidth] = useState(0);

    const statusPill =
        status == null ? (
            <LoadingSkeleton width={100} height={32} borderRadius={9999} />
        ) : onStatusPress != null ? (
            <Pressable onPress={onStatusPress}>
                <SpendingStatusPill status={status} />
            </Pressable>
        ) : (
            <SpendingStatusPill status={status} />
        );

    const spendSection = (
        <>
            <XStack
                justifyContent="space-between"
                width="100%"
                paddingHorizontal={16}
            >
                <View flex={1} minWidth={0}>
                    <LabelRegular
                        color="$gray1000"
                        fontSize={12}
                        lineHeight={16}
                    >
                        {currentSpendLabel}
                    </LabelRegular>
                    {isLoading || currentSpendCents == null ? (
                        <LoadingSkeleton
                            width={50}
                            height={32}
                            marginVertical={8}
                        />
                    ) : (
                        <LabelRegular
                            color="$gray1200"
                            fontSize={24}
                            lineHeight={32}
                            fontWeight="500"
                        >
                            {centsToFormattedDollars(currentSpendCents)}
                        </LabelRegular>
                    )}
                </View>
                <View flex={1} minWidth={0} alignItems="flex-end">
                    <LabelRegular
                        color="$gray1000"
                        fontSize={12}
                        lineHeight={16}
                    >
                        Budget
                    </LabelRegular>
                    {isLoading || budgetCents == null ? (
                        <LoadingSkeleton
                            width={50}
                            height={32}
                            marginVertical={8}
                        />
                    ) : (
                        <LabelRegular
                            color="$gray1200"
                            fontSize={24}
                            lineHeight={32}
                            fontWeight="500"
                        >
                            {centsToFormattedDollars(budgetCents)}
                        </LabelRegular>
                    )}
                </View>
            </XStack>
            <View
                height={GRAPH_HEIGHT}
                onLayout={(e) => {
                    const w = e.nativeEvent.layout.width;
                    if (w > 0) setChartWidth(w);
                }}
            >
                {chartWidth > 0 && (
                    <BudgetGraph
                        budget={budgetCents ?? 0}
                        spendData={spendData}
                        period={period}
                        width={chartWidth}
                        height={GRAPH_HEIGHT}
                        lineColor="$green900"
                        lineThickness={2}
                        anchorDate={anchorDate ?? new Date()}
                        isOverBudget={isOverBudget}
                        isLoading={isLoading}
                    />
                )}
            </View>
        </>
    );

    return (
        <View
            backgroundColor="$gray200"
            borderRadius={24}
            overflow="hidden"
            width="100%"
        >
            <XStack
                alignItems="center"
                gap={12}
                justifyContent="space-between"
                minHeight={56}
                paddingHorizontal={24}
                paddingVertical={24}
            >
                <View flex={1} minWidth={0}>
                    <YStack gap={8}>
                        <LabelRegular
                            color="$gray1200"
                            fontSize={18}
                            lineHeight={24}
                            fontWeight="600"
                        >
                            {title}
                        </LabelRegular>
                        {headerSlot}
                    </YStack>
                </View>
                {statusPill}
            </XStack>

            <View backgroundColor="$gray100" paddingTop={16} width="100%">
                {onGraphPress != null ? (
                    <Pressable
                        onPress={onGraphPress}
                        style={{ cursor: 'pointer' }}
                    >
                        {spendSection}
                    </Pressable>
                ) : (
                    <View>{spendSection}</View>
                )}
            </View>

            <YStack
                backgroundColor="$gray200"
                gap={8}
                paddingHorizontal={24}
                paddingVertical={24}
            >
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                >
                    <BodyMediumRegular color="$gray1000">
                        Left in budget
                    </BodyMediumRegular>
                    {isLoading || leftInBudgetCents == null ? (
                        <LoadingSkeleton width={50} height={24} />
                    ) : (
                        <BodyMediumRegular color="$gray1200">
                            {centsToFormattedDollars(
                                Math.max(0, leftInBudgetCents),
                            )}
                        </BodyMediumRegular>
                    )}
                </XStack>
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                >
                    <BodyMediumRegular color="$gray1000">
                        Time left
                    </BodyMediumRegular>
                    {isLoading || timeLeft == null ? (
                        <LoadingSkeleton width={50} height={24} />
                    ) : (
                        <BodyMediumRegular color="$gray1200">
                            {timeLeft}
                        </BodyMediumRegular>
                    )}
                </XStack>
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                >
                    <BodyMediumRegular color="$gray1000">
                        {remainingLabel}
                    </BodyMediumRegular>
                    {isLoading || remainingValue == null ? (
                        <LoadingSkeleton width={50} height={24} />
                    ) : (
                        <BodyMediumRegular color="$gray1200">
                            {remainingValue}
                        </BodyMediumRegular>
                    )}
                </XStack>
            </YStack>
        </View>
    );
};
